import React, { useState, useEffect } from 'react';
import { Users, MessageCircle, Zap, CheckCircle } from 'lucide-react';
import { Button, LoadingSpinner } from '../components/ui';
import { PageHeader, ContentCard } from '../components/layout';
import { toast } from '../components/ui/Toaster';
import { premiumService, UserPremiumFeatures, PremiumPurchase } from '../services/premiumService';
import UpgradeModal from '../components/modals/UpgradeModal';
import PaymentSuccessModal from '../components/modals/PaymentSuccessModal';
import { usePaymentSuccess } from '../hooks/usePaymentSuccess';

const AccountUpgradesPage: React.FC = () => {
  const [features, setFeatures] = useState<UserPremiumFeatures | null>(null);
  const [purchases, setPurchases] = useState<PremiumPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<'EXTRA_GROUP_SLOTS' | 'EXTENDED_GROUP_SIZE' | 'CHAT_ENHANCEMENTS'>('EXTRA_GROUP_SLOTS');
  const paymentSuccess = usePaymentSuccess();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [userFeatures, userPurchases] = await Promise.all([
        premiumService.getUserFeatures(),
        premiumService.getPurchaseHistory(),
      ]);
      setFeatures(userFeatures);
      setPurchases(userPurchases);
    } catch (error) {
      console.error('Failed to load upgrade data:', error);
      toast.error('Fout', 'Kon upgrade gegevens niet laden');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradeClick = (featureType: 'EXTRA_GROUP_SLOTS' | 'EXTENDED_GROUP_SIZE' | 'CHAT_ENHANCEMENTS') => {
    setSelectedFeature(featureType);
    setShowUpgradeModal(true);
  };

  const handleUpgradeComplete = () => {
    loadData(); // Reload data after upgrade
  };

  // Handle payment success from URL parameters
  useEffect(() => {
    if (paymentSuccess.isSuccess) {
      toast.success('Succes!', 'Je upgrade is geactiveerd!');
      loadData(); // Reload data to show updated features
    }
  }, [paymentSuccess.isSuccess]);

  const getUpgradeIcon = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return <Users className="w-6 h-6 text-neon-cyan" />;
      case 'EXTENDED_GROUP_SIZE':
        return <Zap className="w-6 h-6 text-neon-lime" />;
      case 'CHAT_ENHANCEMENTS':
        return <MessageCircle className="w-6 h-6 text-neon-violet" />;
      default:
        return <Users className="w-6 h-6 text-neon-cyan" />;
    }
  };

  const getUpgradeName = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Extra groep slots';
      case 'EXTENDED_GROUP_SIZE':
        return 'Groep uitbreiding';
      case 'CHAT_ENHANCEMENTS':
        return 'Chat uitbreidingen';
      default:
        return 'Onbekende upgrade';
    }
  };

  const getUpgradeDescription = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return 'Join meer groepen tegelijkertijd (+3 slots per keer)';
      case 'EXTENDED_GROUP_SIZE':
        return 'Verhoog groep capaciteit met +5 leden';
      case 'CHAT_ENHANCEMENTS':
        return 'Gebruik GIFs in chat';
      default:
        return '';
    }
  };

  const isUpgradeActive = (featureType: string) => {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return features?.extraGroupSlots || false;
      case 'CHAT_ENHANCEMENTS':
        return features?.chatEnhancements || false;
      default:
        return false;
    }
  };

  const getUserGroupLimit = () => {
    // Calculate current group limit based on completed EXTRA_GROUP_SLOTS purchases
    // Note: the backend includes `quantity` on purchase records, but the
    // PremiumPurchase type (services/premiumService.ts) is outside this fix's
    // scope, so it's widened locally instead of editing that file.
    const completedSlotPurchases = purchases.filter(
      p => p.featureType === 'EXTRA_GROUP_SLOTS' && p.status === 'COMPLETED'
    ) as (PremiumPurchase & { quantity?: number })[];
    const totalPurchases = completedSlotPurchases.reduce((sum, p) => sum + (p.quantity || 1), 0);
    return 3 + (totalPurchases * 3); // Base 3 + 3 per purchase
  };

  const canBuyMoreGroupSlots = () => {
    const currentLimit = getUserGroupLimit();
    return currentLimit < 9; // Max is 9 groups
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-neon-lime/[.12] text-neon-lime';
      case 'PENDING':
        return 'bg-warning-500/15 text-warning-400';
      case 'FAILED':
        return 'bg-neon-pink/[.12] text-neon-pink-soft';
      default:
        return 'bg-white/[.06] text-fg-muted';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Laden..." />
      </div>
    );
  }

  const allUpgrades = ['EXTRA_GROUP_SLOTS', 'CHAT_ENHANCEMENTS'];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upgrades"
        subtitle="Bekijk je actieve upgrades en aankoop geschiedenis"
      />

      {/* Available Upgrades */}
      <ContentCard>
        <h2 className="text-xl font-bold text-fg-bright mb-6">
          Beschikbare upgrades
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {allUpgrades.map((featureType) => {
            const isActive = isUpgradeActive(featureType);
            return (
              <div key={featureType} className={`p-6 rounded-item border ${
                isActive
                  ? 'border-neon-lime/25 bg-neon-lime/[.06]'
                  : 'border-white/[.08] bg-white/[.03]'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {getUpgradeIcon(featureType)}
                    <span className="font-semibold text-fg-bright">
                      {getUpgradeName(featureType)}
                    </span>
                  </div>
                  {isActive && (
                    <span className="sf-badge-lime">
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      Actief
                    </span>
                  )}
                </div>

                <p className="text-sm text-fg-body mb-4">
                  {getUpgradeDescription(featureType)}
                </p>

                {(() => {
                  // Chat enhancements: hide button when already purchased (one-time only)
                  if (featureType === 'CHAT_ENHANCEMENTS' && isActive) {
                    return (
                      <div className="text-center py-2">
                        <span className="text-sm text-neon-lime font-medium">Geactiveerd</span>
                      </div>
                    );
                  }

                  // Group slots: hide button when user reached max (9 groups)
                  if (featureType === 'EXTRA_GROUP_SLOTS' && !canBuyMoreGroupSlots()) {
                    return (
                      <div className="text-center py-2">
                        <span className="text-sm text-neon-lime font-medium">Maximum bereikt</span>
                      </div>
                    );
                  }

                  // Show buy button for all other cases
                  return (
                    <Button
                      size="sm"
                      onClick={() => handleUpgradeClick(featureType as any)}
                      className="w-full"
                    >
                      Kopen - €4,95
                    </Button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </ContentCard>

      {/* Purchase History */}
      <ContentCard>
        <h2 className="text-xl font-bold text-fg-bright mb-6">
          Aankoop geschiedenis
        </h2>

        {purchases.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-fg-muted italic">Nog geen aankopen gedaan</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[.08]">
                  <th className="text-left py-3 px-4 sf-label-mono">Datum</th>
                  <th className="text-left py-3 px-4 sf-label-mono">Upgrade</th>
                  <th className="text-left py-3 px-4 sf-label-mono">Betaling id</th>
                  <th className="text-left py-3 px-4 sf-label-mono">Status</th>
                  <th className="text-right py-3 px-4 sf-label-mono">Bedrag</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr key={purchase.id} className="border-b border-white/[.06] hover:bg-white/[.04] transition-colors">
                    <td className="py-3 px-4 text-sm text-fg-body">
                      {formatDate(purchase.createdAt)}
                    </td>
                    <td className="py-3 px-4 text-sm text-fg-soft">
                      {getUpgradeName(purchase.featureType)}
                    </td>
                    <td className="py-3 px-4 text-xs font-mono text-fg-meta">
                      {purchase.paymentId || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-xs font-mono font-semibold uppercase px-2 py-1 rounded-full ${getStatusColor(purchase.status)}`}>
                        {purchase.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-semibold text-fg-bright">
                      €{Number(purchase.amount).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ContentCard>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        upgradeType={selectedFeature}
        onUpgradeComplete={handleUpgradeComplete}
      />

      {/* Payment Success Modal */}
      {paymentSuccess.isSuccess && paymentSuccess.featureType && paymentSuccess.amount && (
        <PaymentSuccessModal
          isOpen={true}
          onClose={() => {}}
          featureType={paymentSuccess.featureType}
          amount={paymentSuccess.amount}
          onContinue={() => window.location.reload()}
        />
      )}
    </div>
  );
};

export default AccountUpgradesPage;